import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  build,
  compatible,
  findItem,
  newTrack,
  findSnap,
  itemEnd,
  quantize,
  sequenceDuration,
  snapPoints,
  type Sequence,
  type TimelineItem,
  type Track,
  type TrackKind,
} from '@easyedit/timeline'
import type { MediaAsset } from '../../../lib/api/types'
import { useEditorStore } from '../../../stores/editorStore'
import { Clip } from './Clip'
import { Playhead } from './Playhead'
import { Ruler } from './Ruler'
import { TrackHeader } from './TrackHeader'
import { KeyframeLane } from './KeyframeLane'
import { isTextEntry } from '../../../lib/dom/focus'

const HEADER_WIDTH = 148
const RULER_HEIGHT = 28
const MIN_TIMELINE_HEIGHT = 160
const MAX_TIMELINE_HEIGHT = 640
const DEFAULT_TIMELINE_HEIGHT = 280
/** Tolerância de encaixe, em pixels — constante na tela, não no tempo. */
const SNAP_PIXELS = 8

interface DragState {
  mode: 'move' | 'trim-start' | 'trim-end'
  itemId: string
  trackId: string
  startX: number
  startY: number
  originalStart: number
  originalEnd: number
  /** Preview local durante o arraste; o comando só é emitido ao soltar. */
  preview: { start: number; end: number; trackId: string } | null
}

/**
 * A track sob o ponteiro, quando o item cabe nela.
 *
 * Uma track incompatível devolve a de origem — o clip fica onde está em vez de recusar
 * a soltura. É o mesmo teste que o reducer faz ao aplicar `MOVE_ITEMS`, importado dele:
 * duas respostas diferentes para "pode?" seriam um arraste aceito na tela e recusado
 * pelo servidor meio segundo depois.
 */
function trackUnderPointer(
  sequence: Sequence,
  lanes: Track[],
  item: TimelineItem,
  clientY: number,
  container: HTMLElement | null,
  fallbackTrackId: string,
): string {
  if (!container) return fallbackTrackId
  const box = container.getBoundingClientRect()
  let offset = box.top - container.scrollTop + RULER_HEIGHT
  for (const lane of lanes) {
    if (clientY >= offset && clientY < offset + lane.height) {
      if (lane.locked || !compatible(lane, item)) return fallbackTrackId
      return lane.id
    }
    offset += lane.height
  }
  return fallbackTrackId
}

export function Timeline({ assets }: { assets: Map<string, MediaAsset> }) {
  const scrollRef = useRef<HTMLDivElement>(null)
  /**
   * Coluna dos cabeçalhos.
   *
   * Ela NÃO rola: é deslocada para acompanhar a rolagem vertical das faixas. Enquanto
   * não acompanhava, bastava a timeline ficar mais alta que o painel — quatro tracks já
   * bastam — para o cabeçalho parar de corresponder à faixa ao lado, e o usuário
   * silenciar a track errada.
   */
  const headersRef = useRef<HTMLDivElement>(null)
  const [drag, setDrag] = useState<DragState | null>(null)
  const [height, setHeight] = useState(DEFAULT_TIMELINE_HEIGHT)
  const dragRef = useRef<DragState | null>(null)
  dragRef.current = drag

  const sequence = useEditorStore((s) => s.sequence)
  const zoom = useEditorStore((s) => s.zoom)
  const setZoom = useEditorStore((s) => s.setZoom)
  const selected = useEditorStore((s) => s.selectedItemIds)
  const select = useEditorStore((s) => s.select)
  const clearSelection = useEditorStore((s) => s.clearSelection)
  const dispatch = useEditorStore((s) => s.dispatch)
  const setPlayhead = useEditorStore((s) => s.setPlayhead)
  const snapEnabled = useEditorStore((s) => s.snapEnabled)
  const toggleSnap = useEditorStore((s) => s.toggleSnap)
  const rippleEnabled = useEditorStore((s) => s.rippleEnabled)
  const toggleRipple = useEditorStore((s) => s.toggleRipple)
  const selectionRange = useEditorStore((s) => s.selectionRange)
  const setRange = useEditorStore((s) => s.setRange)

  const duration = sequence ? sequenceDuration(sequence) : 0
  // Sempre alguma folga à direita: sem ela, arrastar um clip para além do fim seria
  // impossível porque não haveria onde soltar.
  const contentWidth = Math.max(1200, (duration + 8) * zoom)

  const tracks = useMemo(
    () => (sequence ? [...sequence.tracks].sort((a, b) => b.index - a.index) : []),
    [sequence],
  )

  const snapTargets = useMemo(() => {
    if (!sequence || !snapEnabled) return []
    return snapPoints(sequence, drag ? [drag.itemId] : [])
  }, [sequence, snapEnabled, drag])

  const applySnap = useCallback(
    (time: number): number => {
      if (!snapEnabled) return time
      const snapped = findSnap(snapTargets, time, SNAP_PIXELS / zoom)
      return snapped ?? time
    },
    [snapEnabled, snapTargets, zoom],
  )

  /**
   * Troca uma track de lugar com a vizinha.
   *
   * `REORDER_TRACKS` recebe a ordem INTEIRA, do fundo para o topo — reindexar a lista
   * toda de uma vez é o que mantém os índices densos e sem empate, que é o que a
   * composição depende para ser determinística.
   */
  const moveTrack = useCallback(
    (trackId: string, direction: -1 | 1) => {
      if (!sequence) return
      const order = [...sequence.tracks].sort((a, b) => a.index - b.index).map((t) => t.id)
      const from = order.indexOf(trackId)
      const to = from + direction
      if (from < 0 || to < 0 || to >= order.length) return
      const next = [...order]
      ;[next[from], next[to]] = [next[to]!, next[from]!]
      void dispatch(build.reorderTracks({ sequenceId: sequence.id }, next))
    },
    [sequence, dispatch],
  )

  /**
   * As três operações de intervalo.
   *
   * Todas existiam no reducer desde o primeiro dia e nenhuma tinha como ser pedida:
   * eram três comandos sem produtor. "Cortar" fecha o buraco (é o corte por
   * transcrição, aplicado à mão), "extrair" o mantém, "espaçar" abre um vazio.
   */
  const rangeAction = useCallback(
    (kind: 'delete' | 'lift' | 'gap') => {
      const state = useEditorStore.getState()
      const range = state.selectionRange
      if (!sequence || !range || range.end <= range.start) return
      const ctx = { sequenceId: sequence.id }
      if (kind === 'delete') void dispatch(build.deleteRange(ctx, range.start, range.end))
      else if (kind === 'lift') void dispatch(build.liftRange(ctx, range.start, range.end))
      else void dispatch(build.insertGap(ctx, range.start, range.end - range.start))
      state.setRange(null)
    },
    [sequence, dispatch],
  )

  const addTrack = useCallback(
    (kind: TrackKind) => {
      if (!sequence) return
      void dispatch(build.addTrack({ sequenceId: sequence.id }, newTrack(sequence.tracks, kind)))
    },
    [sequence, dispatch],
  )

  /** O clip cuja curva a pista de keyframes mostra: só com um selecionado. */
  const keyframeItem = useMemo(() => {
    if (!sequence || selected.length !== 1) return null
    return findItem(sequence, selected[0]!)?.item ?? null
  }, [sequence, selected])

  /** O clip que está sendo arrastado PARA uma track que não é a dele. */
  const incomingFor = useCallback(
    (trackId: string): { item: TimelineItem } | null => {
      if (!sequence || !drag?.preview) return null
      if (drag.preview.trackId !== trackId || drag.trackId === trackId) return null
      const found = findItem(sequence, drag.itemId)
      if (!found) return null
      return {
        item: {
          ...found.item,
          start: drag.preview.start,
          duration: Math.max(0.04, drag.preview.end - drag.preview.start),
        },
      }
    },
    [sequence, drag],
  )

  // ---- arraste e trim ----------------------------------------------------

  const onClipPointerDown = useCallback(
    (event: React.PointerEvent, item: TimelineItem, mode: DragState['mode']) => {
      if (!sequence) return
      const found = findItem(sequence, item.id)
      if (!found) return

      event.preventDefault()
      select([item.id], event.shiftKey)
      setDrag({
        mode,
        itemId: item.id,
        trackId: found.track.id,
        startX: event.clientX,
        startY: event.clientY,
        originalStart: item.start,
        originalEnd: itemEnd(item),
        preview: null,
      })
    },
    [sequence, select],
  )

  useEffect(() => {
    if (!drag || !sequence) return

    const onMove = (event: PointerEvent) => {
      const current = dragRef.current
      if (!current) return
      const deltaTime = (event.clientX - current.startX) / zoom

      if (current.mode === 'move') {
        const rawStart = Math.max(0, current.originalStart + deltaTime)
        const snappedStart = applySnap(rawStart)
        // Encaixar também pela borda direita: alinhar o FIM de um clip ao início do
        // próximo é tão comum quanto alinhar o começo.
        const length = current.originalEnd - current.originalStart
        const snappedEnd = applySnap(rawStart + length) - length
        const start = Math.abs(snappedStart - rawStart) <= Math.abs(snappedEnd - rawStart)
          ? snappedStart
          : snappedEnd
        const source = findItem(sequence, current.itemId)
        const trackId = source
          ? trackUnderPointer(sequence, tracks, source.item, event.clientY, scrollRef.current, current.trackId)
          : current.trackId
        setDrag({ ...current, preview: { start: Math.max(0, start), end: Math.max(0, start) + length, trackId } })
      } else if (current.mode === 'trim-start') {
        const time = applySnap(Math.max(0, current.originalStart + deltaTime))
        setDrag({ ...current, preview: { start: Math.min(time, current.originalEnd - 0.04), end: current.originalEnd, trackId: current.trackId } })
      } else {
        const time = applySnap(current.originalEnd + deltaTime)
        setDrag({ ...current, preview: { start: current.originalStart, end: Math.max(time, current.originalStart + 0.04), trackId: current.trackId } })
      }
    }

    const onUp = () => {
      const current = dragRef.current
      setDrag(null)
      if (!current?.preview || !sequence) return

      const fps = sequence.frameRate
      const ctx = { sequenceId: sequence.id }

      if (current.mode === 'move') {
        const start = quantize(current.preview.start, fps)
        const trackId = current.preview.trackId
        const sameTrack = trackId === current.trackId
        const delta = start - current.originalStart
        if (sameTrack && Math.abs(delta) < 1e-6) return

        // Todos os selecionados andam o MESMO delta. É o que faz um grupo (ou uma
        // seleção múltipla) se mover como um bloco em vez de só o clip sob o ponteiro.
        // A troca de track, porém, vale só para o arrastado: mandar os irmãos junto
        // empilharia itens de tracks diferentes numa só.
        const selectedNow = useEditorStore.getState().selectedItemIds
        const ids = selectedNow.includes(current.itemId) ? selectedNow : [current.itemId]
        const moves = ids
          .map((itemId) => {
            const found = findItem(sequence, itemId)
            if (!found) return null
            const isDragged = itemId === current.itemId
            return {
              itemId,
              start: Math.max(0, quantize(found.item.start + delta, fps)),
              ...(isDragged && !sameTrack ? { trackId } : {}),
            }
          })
          .filter((m): m is { itemId: string; start: number; trackId?: string } => m !== null)

        void dispatch(build.moveItems(ctx, moves, rippleEnabled ? 'insert' : 'overwrite'))
      } else if (current.mode === 'trim-start') {
        const time = quantize(current.preview.start, fps)
        if (Math.abs(time - current.originalStart) < 1e-6) return
        void dispatch(build.trim(ctx, current.itemId, 'start', time, rippleEnabled))
      } else {
        const time = quantize(current.preview.end, fps)
        if (Math.abs(time - current.originalEnd) < 1e-6) return
        void dispatch(build.trim(ctx, current.itemId, 'end', time, rippleEnabled))
      }
    }

    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
  }, [drag, sequence, zoom, applySnap, dispatch, rippleEnabled, tracks])

  // ---- teclado -----------------------------------------------------------

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isTextEntry(event.target)) return
      if (!sequence) return

      const state = useEditorStore.getState()
      const ctx = { sequenceId: sequence.id }
      const playhead = state.playhead

      // S: dividir no playhead — o atalho mais usado de qualquer editor.
      if (event.key.toLowerCase() === 's' && !event.metaKey && !event.ctrlKey) {
        event.preventDefault()
        const targets = state.selectedItemIds.length > 0
          ? state.selectedItemIds
          : itemsAtTime(sequence.tracks, playhead)
        for (const itemId of targets) {
          const found = findItem(sequence, itemId)
          if (!found) continue
          if (playhead <= found.item.start || playhead >= itemEnd(found.item)) continue
          void dispatch(build.split(ctx, itemId, playhead))
        }
        return
      }

      if (event.key === 'Delete' || event.key === 'Backspace') {
        if (state.selectedItemIds.length === 0) return
        event.preventDefault()
        // Shift inverte o modo: ripple delete fecha o buraco, delete simples o mantém.
        void dispatch(build.removeItems(ctx, state.selectedItemIds, event.shiftKey ? !rippleEnabled : rippleEnabled))
        clearSelection()
        return
      }

      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'd') {
        if (state.selectedItemIds.length === 0) return
        event.preventDefault()
        void dispatch(build.duplicate(ctx, state.selectedItemIds))
        return
      }

      // ⌘G agrupa, ⌘⇧G desagrupa. O grupo é o que faz legenda, trilha e clip andarem
      // juntos quando o usuário reposiciona um bloco inteiro.
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'g') {
        event.preventDefault()
        if (state.selectedItemIds.length === 0) return
        if (event.shiftKey) {
          const groups = new Set<string>()
          for (const id of state.selectedItemIds) {
            const found = findItem(sequence, id)
            if (found?.item.groupId) groups.add(found.item.groupId)
          }
          if (groups.size === 0) return
          void dispatch(build.batch(ctx, [...groups].map((g) => build.ungroup(ctx, g))))
        } else if (state.selectedItemIds.length >= 2) {
          void dispatch(build.group(ctx, state.selectedItemIds))
        }
        return
      }

      // I e O marcam entrada e saída no playhead — o par de atalhos que todo editor
      // profissional usa, e o que torna a seleção de intervalo utilizável sem mouse.
      if (event.key.toLowerCase() === 'i' && !event.metaKey && !event.ctrlKey) {
        event.preventDefault()
        const current = state.selectionRange
        state.setRange({ start: playhead, end: Math.max(playhead, current?.end ?? playhead) })
        return
      }
      if (event.key.toLowerCase() === 'o' && !event.metaKey && !event.ctrlKey) {
        event.preventDefault()
        const current = state.selectionRange
        state.setRange({ start: Math.min(playhead, current?.start ?? playhead), end: playhead })
        return
      }
      if (event.key === 'Escape') {
        if (state.selectionRange) {
          event.preventDefault()
          state.setRange(null)
        }
        return
      }

      if (event.key === '+' || event.key === '=') {
        event.preventDefault()
        setZoom(state.zoom * 1.3)
      }
      if (event.key === '-' || event.key === '_') {
        event.preventDefault()
        setZoom(state.zoom / 1.3)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [sequence, dispatch, clearSelection, rippleEnabled, setZoom])

  // Ctrl+scroll faz zoom ancorado no cursor — o gesto padrão em ferramentas de timeline.
  const onWheel = useCallback(
    (event: React.WheelEvent) => {
      if (!event.ctrlKey && !event.metaKey) return
      event.preventDefault()
      const container = scrollRef.current
      if (!container) return
      const rect = container.getBoundingClientRect()
      const pointerX = event.clientX - rect.left + container.scrollLeft
      const timeAtPointer = pointerX / zoom
      const next = Math.min(400, Math.max(2, zoom * (event.deltaY < 0 ? 1.15 : 1 / 1.15)))
      setZoom(next)
      requestAnimationFrame(() => {
        container.scrollLeft = timeAtPointer * next - (event.clientX - rect.left)
      })
    },
    [zoom, setZoom],
  )

  if (!sequence) return <div style={{ height }} className="shrink-0 border-t border-ink-750 bg-ink-850" />

  return (
    <section
      className="shrink-0 border-t border-ink-750 bg-ink-850 flex flex-col relative"
      style={{ height }}
    >
      {/*
        Alça de redimensionamento no topo.

        A altura é do USUÁRIO, não do conteúdo. A primeira tentativa foi crescer sozinha
        quando a pista de keyframes aparecia — e o resultado foi a timeline se mexendo
        embaixo do ponteiro no instante em que o clip era selecionado, bem no começo de
        um arraste. Um layout que pula quando você seleciona algo é pior que um que
        precisa rolar.
      */}
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="Redimensionar a timeline"
        className="absolute -top-1 left-0 right-0 h-2 cursor-ns-resize z-30 group/resize"
        onPointerDown={(event) => {
          event.preventDefault()
          const startY = event.clientY
          const startHeight = height
          const move = (e: PointerEvent) => {
            setHeight(Math.min(MAX_TIMELINE_HEIGHT, Math.max(MIN_TIMELINE_HEIGHT, startHeight + (startY - e.clientY))))
          }
          const up = () => {
            window.removeEventListener('pointermove', move)
            window.removeEventListener('pointerup', up)
          }
          window.addEventListener('pointermove', move)
          window.addEventListener('pointerup', up)
        }}
      >
        <div className="absolute inset-x-0 top-1 h-px bg-transparent group-hover/resize:bg-accent/60 transition-colors" />
      </div>
      <div className="h-9 shrink-0 border-b border-ink-750 flex items-center gap-1 px-2">
        <span className="label mr-1">Timeline</span>
        <ToggleButton active={snapEnabled} onClick={toggleSnap} title="Encaixar nas bordas (snap)">
          Snap
        </ToggleButton>
        <ToggleButton active={rippleEnabled} onClick={toggleRipple} title="Fechar o espaço ao remover ou mover">
          Ripple
        </ToggleButton>
        <div className="w-px h-4 bg-ink-700 mx-1" />
        <button className="btn-ghost" onClick={() => setZoom(zoom / 1.4)} title="Menos zoom (−)">−</button>
        <span className="font-mono text-2xs text-ink-500 tnum w-14 text-center">
          {zoom.toFixed(0)} px/s
        </span>
        <button className="btn-ghost" onClick={() => setZoom(zoom * 1.4)} title="Mais zoom (+)">+</button>
        <div className="w-px h-4 bg-ink-700 mx-1" />
        <AddTrackMenu onAdd={addTrack} />

        {selectionRange && (
          <>
            <div className="w-px h-4 bg-ink-700 mx-1" />
            <span className="font-mono text-2xs text-accent tnum">
              {(selectionRange.end - selectionRange.start).toFixed(2)}s
            </span>
            <button
              className="btn-ghost text-2xs"
              onClick={() => rangeAction('delete')}
              title="Remover o intervalo e fechar o buraco"
            >
              Cortar
            </button>
            <button
              className="btn-ghost text-2xs"
              onClick={() => rangeAction('lift')}
              title="Remover o intervalo deixando o buraco"
            >
              Extrair
            </button>
            <button
              className="btn-ghost text-2xs"
              onClick={() => rangeAction('gap')}
              title="Abrir espaço do tamanho do intervalo"
            >
              Espaçar
            </button>
            <button className="btn-ghost text-2xs" onClick={() => setRange(null)} title="Limpar (Esc)">
              ✕
            </button>
          </>
        )}

        <div className="flex-1" />
        <span className="font-mono text-2xs text-ink-500 hidden md:block">
          S dividir · I/O intervalo · ⌘G agrupar · Del remover · ⌘D duplicar
        </span>
      </div>

      <div className="flex-1 min-h-0 flex">
        <div className="shrink-0 border-r border-ink-750 bg-ink-850" style={{ width: HEADER_WIDTH }}>
          <div className="border-b border-ink-750" style={{ height: RULER_HEIGHT }} />
          <div className="overflow-hidden">
            <div ref={headersRef} className="will-change-transform">
              {tracks.map((track) => (
                <TrackHeader key={track.id} track={track} sequence={sequence} onMove={moveTrack} />
              ))}
            </div>
          </div>
        </div>

        <div
          ref={scrollRef}
          className="flex-1 min-w-0 overflow-x-auto overflow-y-auto relative"
          onWheel={onWheel}
          onScroll={(event) => {
            const headers = headersRef.current
            if (headers) headers.style.transform = `translateY(${-event.currentTarget.scrollTop}px)`
          }}
          onPointerDown={(event) => {
            // Clique no vazio limpa a seleção — sem isso, a seleção "gruda".
            if (event.target === event.currentTarget) clearSelection()
          }}
        >
          <div className="relative" style={{ width: contentWidth }}>
            <Ruler
              zoom={zoom}
              duration={duration}
              width={contentWidth}
              frameRate={sequence.frameRate}
              onSeek={setPlayhead}
              onSetRange={setRange}
            />

            {/* A faixa do intervalo atravessa todas as tracks: o que ela marca vale para
                a sequência inteira, não para uma camada. */}
            {selectionRange && (
              <div
                className="absolute z-10 pointer-events-none border-x border-accent bg-accent/10"
                style={{
                  left: selectionRange.start * zoom,
                  width: Math.max(1, (selectionRange.end - selectionRange.start) * zoom),
                  top: RULER_HEIGHT,
                  bottom: 0,
                }}
              />
            )}

            {tracks.map((track) => (
              <TrackLane
                key={track.id}
                track={track}
                zoom={zoom}
                assets={assets}
                selected={selected}
                drag={drag}
                incoming={incomingFor(track.id)}
                onClipPointerDown={onClipPointerDown}
              />
            ))}

            {/* A pista de keyframes é uma lente sobre o clip selecionado, não uma track
                da sequência — por isso vem depois das tracks e some sem seleção. */}
            {keyframeItem && (
              <KeyframeLane sequence={sequence} item={keyframeItem} zoom={zoom} />
            )}

            {sequence.markers.map((marker) => (
              <div
                key={marker.id}
                className="absolute z-20 pointer-events-none"
                style={{ left: marker.time * zoom, top: RULER_HEIGHT - 8 }}
                title={marker.name}
              >
                <div className="w-2 h-2 rotate-45" style={{ background: marker.color }} />
              </div>
            ))}

            <Playhead zoom={zoom} offsetTop={RULER_HEIGHT} />
          </div>
        </div>
      </div>
    </section>
  )
}

function TrackLane({
  track,
  zoom,
  assets,
  selected,
  drag,
  incoming,
  onClipPointerDown,
}: {
  track: Track
  zoom: number
  assets: Map<string, MediaAsset>
  selected: string[]
  drag: DragState | null
  incoming: { item: TimelineItem } | null
  onClipPointerDown: (event: React.PointerEvent, item: TimelineItem, mode: DragState['mode']) => void
}) {
  const isDropTarget = Boolean(incoming)
  return (
    <div
      className={`relative border-b border-ink-800 transition-colors
        ${track.hidden ? 'opacity-40' : ''} ${isDropTarget ? 'bg-accent-soft/30' : ''}`}
      style={{ height: track.height }}
    >
      {track.items.map((item) => {
        // Durante o arraste, o clip segue o ponteiro sem emitir comando: o servidor só
        // é envolvido quando o usuário solta.
        const preview = drag?.itemId === item.id ? drag.preview : null
        // Arrastando para outra track, o clip some desta e aparece na de destino.
        if (preview && preview.trackId !== track.id) return null
        const displayed = preview
          ? { ...item, start: preview.start, duration: Math.max(0.04, preview.end - preview.start) }
          : item
        return (
          <Clip
            key={item.id}
            item={displayed}
            track={track}
            asset={item.source ? assets.get(item.source.assetId) : undefined}
            zoom={zoom}
            height={track.height}
            selected={selected.includes(item.id)}
            onPointerDown={onClipPointerDown}
          />
        )
      })}

      {/* O clip vindo de outra track: desenhado aqui enquanto o ponteiro está sobre
          esta faixa, para o usuário ver onde vai cair antes de soltar. */}
      {incoming && (
        <Clip
          item={incoming.item}
          track={track}
          asset={incoming.item.source ? assets.get(incoming.item.source.assetId) : undefined}
          zoom={zoom}
          height={track.height}
          selected
          onPointerDown={onClipPointerDown}
        />
      )}
    </div>
  )
}

const NEW_TRACK_KINDS: Array<{ kind: TrackKind; label: string }> = [
  { kind: 'video', label: 'Vídeo' },
  { kind: 'overlay', label: 'Overlay' },
  { kind: 'text', label: 'Texto' },
  { kind: 'audio', label: 'Áudio' },
  { kind: 'music', label: 'Música' },
  { kind: 'sfx', label: 'SFX' },
]

function AddTrackMenu({ onAdd }: { onAdd: (kind: TrackKind) => void }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="relative">
      <button className="btn-ghost text-2xs" onClick={() => setOpen((v) => !v)} title="Nova track">
        + Track
      </button>
      {open && (
        <>
          {/* A cortina fecha o menu em qualquer clique fora, sem listener global. */}
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute z-40 top-7 left-0 w-32 rounded border border-ink-700 bg-ink-900 py-1 shadow-lg">
            {NEW_TRACK_KINDS.map((entry) => (
              <button
                key={entry.kind}
                className="w-full text-left px-2.5 py-1 text-2xs text-ink-300 hover:bg-ink-800 hover:text-ink-100"
                onClick={() => { onAdd(entry.kind); setOpen(false) }}
              >
                {entry.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function ToggleButton({
  active,
  onClick,
  title,
  children,
}: {
  active: boolean
  onClick: () => void
  title: string
  children: React.ReactNode
}) {
  return (
    <button
      className={`btn text-2xs ${active ? 'bg-accent-soft text-accent' : 'text-ink-400 hover:bg-ink-750'}`}
      onClick={onClick}
      title={title}
      aria-pressed={active}
    >
      {children}
    </button>
  )
}

function itemsAtTime(tracks: Track[], time: number): string[] {
  const ids: string[] = []
  for (const track of tracks) {
    if (track.locked) continue
    for (const item of track.items) {
      if (time > item.start && time < itemEnd(item)) ids.push(item.id)
    }
  }
  return ids
}
